import React, { useState, useCallback, useEffect } from 'react';
import './AdaniDashboard.css';
import { Toast } from './shared';
import { MOCK_USER } from './mockData';
import { Topbar, Sidebar } from './Layout';
import { NewLeadModal, LinkLeadModal } from './Modals';
import DashboardPage from './pages/DashboardPage';
import CapturePage from './pages/CapturePage';
import RecordingsPage from './pages/RecordingsPage';
import LeadsPage from './pages/LeadsPage';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import InsightsPage from './pages/InsightsPage';
import MilestonesPage from './pages/MilestonesPage';
import BottlenecksPage from './pages/BottlenecksPage';
import api from '../utils/api';

function PlaceholderPage({ title, subtitle }) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">{title}</h1>
        <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">{subtitle}</p>
      </div>
      <div className="glass-card-static rounded-2xl p-5">
        <p className="text-[13px] text-[var(--adani-text-tertiary)] text-center py-10">No data yet.</p>
      </div>
    </div>
  );
}

export default function AdaniDashboard() {
  const [authState, setAuthState] = useState('landing'); // 'landing' | 'login' | 'app'
  const [page, setPage] = useState('dashboard');
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [sessionUser, setSessionUser] = useState({ email: '', password: '' });
  const [counts, setCounts] = useState({ recordings: 0, leads: 0, insights: 0, milestones: 0, bottlenecks: 0 });

  const fetchCounts = useCallback(async (userEmail) => {
    try {
      const [recordingsRes, leadsRes] = await Promise.all([
        api.get('/adani/recordings', { params: { limit: 1000, user_email: userEmail || undefined } }).catch(() => ({ data: [] })),
        api.get('/adani/leads', { params: { limit: 1000 } }).catch(() => ({ data: [] })),
      ]);

      const parseArray = (res) => {
        const d = res?.data;
        if (Array.isArray(d)) return d;
        if (d?.data && Array.isArray(d.data)) return d.data;
        if (d?.recordings && Array.isArray(d.recordings)) return d.recordings;
        if (d?.leads && Array.isArray(d.leads)) return d.leads;
        if (d?.body?.data && Array.isArray(d.body.data)) return d.body.data;
        return [];
      };

      const recordingsArr = parseArray(recordingsRes);
      const leadsArr = parseArray(leadsRes);

      setCounts(prev => ({
        ...prev,
        recordings: recordingsArr.length,
        leads: leadsArr.length,
      }));
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  }, []);

  const loggedIn = authState === 'app';

  useEffect(() => {
    if (loggedIn) {
      fetchCounts(sessionUser.email);
    }
  }, [loggedIn, sessionUser.email, fetchCounts]);

  const refreshCounts = useCallback(() => {
    if (loggedIn) fetchCounts(sessionUser.email);
  }, [loggedIn, sessionUser.email, fetchCounts]);

  const openModal = useCallback(m => setModal(m), []);
  const closeModal = useCallback(() => setModal(null), []);
  const showToast = useCallback(msg => { setToast(msg); setTimeout(() => setToast(null), 3000); }, []);
  const onLogout = useCallback(() => {
    setAuthState('landing');
    setSessionUser({ email: '', password: '' });
    setCounts({ recordings: 0, leads: 0, insights: 0, milestones: 0, bottlenecks: 0 });
  }, []);
  const handleLogin = useCallback((creds) => {
    setSessionUser(creds);
    setAuthState('app');
  }, []);

  if (authState === 'landing') return <LandingPage onSignIn={() => setAuthState('login')} />;
  if (authState === 'login') return <LoginPage onLogin={handleLogin} onBack={() => setAuthState('landing')} />;

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <DashboardPage counts={counts} />;
      case 'capture': return <CapturePage openModal={openModal} setPage={setPage} onCaptureComplete={refreshCounts} />;
      case 'recordings': return <RecordingsPage userEmail={sessionUser.email} onDataChange={refreshCounts} />;
      case 'leads': return <LeadsPage openModal={openModal} onDataChange={refreshCounts} />;
      case 'insights': return <InsightsPage counts={counts} setCounts={setCounts} />;
      case 'milestones': return <MilestonesPage counts={counts} setCounts={setCounts} />;
      case 'bottlenecks': return <BottlenecksPage counts={counts} setCounts={setCounts} />;
      case 'programmes': return <PlaceholderPage title="Programmes" subtitle="Track and manage all programme timelines" />;
      case 'lessons': return <PlaceholderPage title="Lessons Learnt" subtitle="Repository of insights from past programmes" />;
      default: return <DashboardPage counts={counts} />;
    }
  };

  return (
    <div className="pip-root">
      <Topbar user={MOCK_USER} onLogout={onLogout} onRefresh={() => { showToast('Dashboard refreshed'); refreshCounts(); }} />
      <Sidebar page={page} setPage={setPage} onLogout={onLogout} counts={counts} />
      <main style={{ marginLeft: 220, paddingTop: 56 }} className="min-h-screen text-left">
        <div className="p-6 max-w-[1200px]">
          {renderPage()}
        </div>
      </main>
      <NewLeadModal modal={modal} closeModal={closeModal} showToast={showToast} />
      <LinkLeadModal modal={modal} closeModal={closeModal} showToast={showToast} />
      <Toast toast={toast} />
    </div>
  );
}
